import React, { useId } from 'react';
import { CheckIcon, PlusIcon, ChevronDownIcon } from '../icons';
import { useComboboxSelector, ComboboxOption } from './hooks/useComboboxSelector';

interface ComboboxSelectorProps<T extends ComboboxOption> {
    value: string;
    onChange: (value: string) => void;
    options: T[];
    loading?: boolean;
    placeholder?: string;
    createLabel?: string;
    onCreate?: (name: string) => Promise<void | string>;
    disabled?: boolean;
    /** Qual campo emitir para onChange: 'id' (UUID) ou 'name' (padrão) */
    valueKey?: 'id' | 'name';
}

export type { ComboboxOption };

export function ComboboxSelector<T extends ComboboxOption>({
    value,
    onChange,
    options,
    loading = false,
    placeholder = 'Selecione ou digite...',
    createLabel = 'Adicionar',
    onCreate,
    disabled = false,
    valueKey = 'name'
}: ComboboxSelectorProps<T>) {
    const inputId = useId();
    const {
        query,
        setQuery,
        isOpen,
        setIsOpen,
        displayValue,
        containerRef,
        filteredOptions,
        canCreate,
        handleSelect,
        handleCreate,
        openDropdown,
        toggleDropdown
    } = useComboboxSelector({ value, onChange, options, onCreate, disabled, valueKey });

    return (
        <div className="relative" ref={containerRef}>
            {/* Input Trigger */}
            <div
                className={`w-full bg-background border border-border rounded-lg px-3 py-2 text-left cursor-pointer focus-within:border-primary flex items-center justify-between transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={(e) => {
                    if (!disabled && e.target === e.currentTarget) {
                        setIsOpen(true);
                    }
                }}
            >
                <input
                    type="text"
                    id={inputId}
                    name={inputId}
                    className="w-full border-none p-0 focus:ring-0 focus:outline-none outline-none bg-transparent text-sm text-text cursor-pointer placeholder-muted"
                    placeholder={placeholder}
                    aria-label={placeholder}
                    autoComplete="off"
                    value={isOpen ? query : displayValue}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled && !isOpen) setIsOpen(true);
                    }}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onFocus={openDropdown}
                    disabled={disabled}
                />
                <ChevronDownIcon
                    className="h-5 w-5 text-muted cursor-pointer"
                    aria-hidden="true"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown();
                    }}
                />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-background border border-border shadow-md max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                    {loading ? (
                        <div className="px-4 py-2 text-sm text-muted">Carregando...</div>
                    ) : (
                        <>
                            {filteredOptions.map((option) => {
                                const isSelected = valueKey === 'id'
                                    ? option.id === value
                                    : option.name === value;
                                return (
                                    <div
                                        key={option.id}
                                        className={`cursor-pointer select-none relative py-2 pl-3 pr-9 transition-colors hover:bg-surface-subtle ${isSelected ? 'bg-primary-subtle text-primary' : 'text-text'}`}
                                        onClick={() => handleSelect(option)}
                                    >
                                        <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                                            {option.name}
                                        </span>
                                        {isSelected && (
                                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary">
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Opção Criar */}
                            {canCreate && (
                                <div
                                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-surface-subtle transition-colors text-primary border-t border-border"
                                    onClick={handleCreate}
                                >
                                    <div className="flex items-center">
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        <span className="font-medium">{createLabel} "{query}"</span>
                                    </div>
                                    <span className="block text-xs text-muted ml-6">Criar novo item</span>
                                </div>
                            )}

                            {filteredOptions.length === 0 && query === '' && (
                                <div className="px-4 py-2 text-sm text-muted">Nenhum item encontrado.</div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
